import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PageBlock, Stack, vars } from '@etg/wings';
import { Helmet } from 'react-helmet';
import { cabinBaggage, checkInBaggage, seatMap, useSeatMapModal } from '@eti/products';
import {
  BoundNew,
  BundleProduct,
  CabinBaggageTripSummary,
  ChargingCurrency,
  CheckedBaggageSummary,
  ExtraProduct,
  IncludedCheckedBaggage,
  PersonalItemTripSummary,
  TravelerPrice,
  TripChange,
  TripCharacteristics,
} from '@eti/types';
import { css } from '@eti/styles';
import { Collection, DevBar } from '@eti/debug';
import { useProperty, useTranslation } from '@eti/providers';
import { useKindly } from '../../../common/custom-hooks/useKindly';
import { isDev } from '../../../common/debug/utils';
import { TRAVELER_DETAILS } from '../../../constants/bookingFlowSteps';
import { DebugButton, DebugCommonComponents } from '../../../common/debug/components';
import TravelerDetailsFormContainer from '../containers/TravelerDetailsFormContainer';
import { getTravelerFormProducts, hasExcludedSystemProvider } from '../utils/productPerTraveler';
import { useScrollTop } from '../../../common/custom-hooks/scrolling';
import StepIndicator from '../../../common/step-indicator/BookingFlowStepIndicator';
import { shouldShowDebugProviderSystemBar } from '../../../utils/debug';
import DebugProviderSystemBar from '../../../widgets/trip-details/components/DebugProviderSystemBar';
import { MULTI_STOP, ONE_WAY, RETURN } from '../../../constants/tripTypesConstants';
import BackToPreviousPageButton from '../../../common/components/BackToPreviousPageButton';
import { routes } from '../../../constants/routesAndApisConstants';
import { flattenProducts } from '../utils/products';
import { COUNTRY_CODES } from '../utils/constants';
import useStopLoss from '../../payment/hooks/useStopLoss';
import DifferentChargingCurrencyNotification from '../../../widgets/different-charging-currency-notification/DifferentChargingCurrencyNotification';
import { getOriginAndDestinationCodesFromBound } from '../../../utils/format';
import { useGetBlue, eventTypes } from '../../../utils/useGetBlue';
import TravelerDetailsNotifications from './TravelerDetailsNotifications';
import EditChoicesLink from './EditChoicesLink';
import InfoReviewModalContainer from './info-review-modal/InfoReviewModalContainer';

const isDebugEnabled = window.spa_variables?.debugEnabled;

const blockStyles = css`
  background-color: ${vars.colors.page.background.primary};
`;

const TravelerDetailsPage = ({
  addedProductsNumber,
  addToCart,
  asyncValidate,
  availableExtraProducts,
  bounds,
  bundles,
  bundlesTD,
  cabinBaggageTripSummary,
  change,
  checkedBaggageSummary,
  climateCompensationPrice,
  countries,
  domesticCountryCode,
  fillForm,
  form,
  handleSubmit,
  headerAndInfoHeight,
  includedCheckedBaggage,
  includedProducts,
  isAncillaryBundlingEnabled,
  isAnyTouched,
  isDomestic,
  isLoading,
  isMultiStop,
  isSubmitting,
  isValid,
  notAvailableProducts,
  paymentMethodPrices,
  personalItemTripSummary,
  products,
  reset,
  selectionKey,
  chargingCurrency,
  shareableUrl,
  shouldShowEmergencyContactInformation,
  travelerNames,
  travelerPricesWithoutPaymentDiscounts,
  travelers,
  tripId,
  tripChange,
  tripCharacteristics = [],
  tripOperatingCarriers,
  tripProviderSystems,
  type,
  voucherAmount,
}) => {
  const [availableProducts, updateAvailableProducts] = useState(products);
  const [isOnSectionOne, setIsOnSectionOne] = useState(true);
  const [showInfoReviewModal, setShowInfoReviewModal] = useState(false);
  useKindly();
  const { isSeatMapModalOpen, setIsSeatMapModalOpen, seatMapCallback, setSeatMapCallback } =
    useSeatMapModal();
  const { t } = useTranslation();
  const { p } = useProperty();
  useScrollTop();
  const showDocumentTypeEnabled = p('TD.Passport.DocumentType.Enabled');
  const isSeatMapProvided =
    p('IbeClient.TravelerDetails.Modal') === 'SEATMAP' &&
    flattenProducts(availableProducts).some((product) => Number(product.id) === seatMap.id);

  const { clearStopLossChange } = useStopLoss();

  useGetBlue(eventTypes.BASKET, getOriginAndDestinationCodesFromBound(bounds[0]));

  useEffect(() => {
    clearStopLossChange();
  }, [clearStopLossChange]);

  useEffect(() => {
    updateAvailableProducts(products);
  }, [products, updateAvailableProducts]);

  useEffect(() => {
    sessionStorage.setItem('showSeatMapStep', isSeatMapProvided);
  }, [isSeatMapProvided]);

  useEffect(() => {
    if (sessionStorage.getItem('hasClosedNotValidVoucherMessage')) {
      sessionStorage.removeItem('hasClosedNotValidVoucherMessage');
    }
  }, []);

  const productsToDisplayPerTraveler = p('IbeClient.ProductsToDisplayPerTraveler');
  const productsPerTravelerExcludedProviderSystems = p(
    'IbeClient.ProductsPerTravelerExcludedProviderSystems',
  );
  const shouldShowProductsPerTraveler =
    p('IbeClient.ShowProductsPerTraveler.Enabled') &&
    !hasExcludedSystemProvider(tripProviderSystems, productsPerTravelerExcludedProviderSystems) &&
    productsToDisplayPerTraveler.length > 0;

  const {
    travelerFormProducts,
    filteredProductsArray,
    filteredIncludedProductsArray,
    isCheckedBaggageAvailableOrIncluded,
    isCabinBaggageAvailableOrIncluded,
  } = getTravelerFormProducts(
    productsToDisplayPerTraveler,
    shouldShowProductsPerTraveler,
    availableProducts,
    includedProducts,
    notAvailableProducts,
    checkInBaggage.id,
    cabinBaggage.id,
  );

  const shouldShowTravelDocumentType =
    showDocumentTypeEnabled &&
    isDomestic &&
    (domesticCountryCode === COUNTRY_CODES.RU || domesticCountryCode === COUNTRY_CODES.BR);

  const handleEditInfo = (elementID) => {
    setShowInfoReviewModal(false);
    setIsOnSectionOne(true);

    if (elementID) {
      setTimeout(() => {
        const targetElement = document.getElementById(elementID);
        if (targetElement) {
          targetElement.setAttribute('tabindex', '-1');
          targetElement.focus();
        }
      }, 0);
    }
  };

  return (
    <>
      <Helmet title={t('PageTitle.rf.travelerdetails.action')} />
      <StepIndicator currentStep={TRAVELER_DETAILS} isSeatMapProvided={isSeatMapProvided} />
      {shouldShowDebugProviderSystemBar(tripProviderSystems) && (
        <PageBlock className={blockStyles}>
          <DebugProviderSystemBar providerSystems={tripProviderSystems} />
        </PageBlock>
      )}
      <PageBlock className={blockStyles} data-testid="travelerDetailsPage" withPaddingY>
        {isDebugEnabled && <div data-testid="debug-trip-placeholder" />}
        <Stack spacing={16}>
          {isOnSectionOne ? (
            p('TD.BackToSearchBtn.Enabled') && (
              <BackToPreviousPageButton
                dataTestId="edit-search-button"
                linkText={t('TD.BackToSearchBtn.Text')}
                route={routes.RESULT}
              />
            )
          ) : (
            <EditChoicesLink onClick={() => setIsOnSectionOne(true)} />
          )}
          {p('IbeClient.DifferentChargingCurrencyNotification.Enabled') && (
            <DifferentChargingCurrencyNotification />
          )}
          <TravelerDetailsNotifications
            bounds={bounds}
            climateCompensationPrice={climateCompensationPrice}
            tripChange={tripChange}
            tripCharacteristics={tripCharacteristics}
          />
          <TravelerDetailsFormContainer
            addToCart={addToCart}
            addedProductsNumber={addedProductsNumber}
            asyncValidate={asyncValidate}
            availableExtraProducts={availableExtraProducts}
            bottomContent={{ isAnyTouched, isValid }}
            bounds={bounds}
            bundleData={bundles?.[0] || {}}
            bundleDataTDPage={bundlesTD?.[0] || {}}
            cabinBaggageTripSummary={cabinBaggageTripSummary}
            chargingCurrency={chargingCurrency}
            checkedBaggageSummary={checkedBaggageSummary}
            countries={countries}
            domesticCountryCode={domesticCountryCode}
            form={form}
            headerAndInfoHeight={headerAndInfoHeight}
            includedCheckedBaggage={includedCheckedBaggage}
            includedProducts={filteredIncludedProductsArray}
            isAncillaryBundlingEnabled={isAncillaryBundlingEnabled}
            isAnyTouched={isAnyTouched}
            isCabinBaggageAvailableOrIncluded={isCabinBaggageAvailableOrIncluded}
            isCheckedBaggageAvailableOrIncluded={isCheckedBaggageAvailableOrIncluded}
            isLoading={isLoading}
            isMultiStop={isMultiStop}
            isOnSectionOne={isOnSectionOne}
            isSeatMapModalOpen={isSeatMapModalOpen}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            paymentMethodPrices={paymentMethodPrices}
            personalItemTripSummary={personalItemTripSummary}
            products={filteredProductsArray}
            seatMapCallback={seatMapCallback}
            selectionKey={selectionKey}
            setIsOnSectionOne={setIsOnSectionOne}
            setIsSeatMapModalOpen={setIsSeatMapModalOpen}
            setSeatMapCallback={setSeatMapCallback}
            setShowInfoReviewModal={setShowInfoReviewModal}
            shareableUrl={shareableUrl}
            shouldShowEmergencyContactInformation={shouldShowEmergencyContactInformation}
            shouldShowProductsPerTraveler={shouldShowProductsPerTraveler}
            shouldShowTravelDocumentType={shouldShowTravelDocumentType}
            travelerFormProducts={travelerFormProducts}
            travelerNames={travelerNames}
            travelerPrices={travelerPricesWithoutPaymentDiscounts}
            travelers={travelers}
            tripCharacteristics={tripCharacteristics}
            tripId={tripId}
            tripOperatingCarriers={tripOperatingCarriers}
            tripProviderSystems={tripProviderSystems}
            type={type}
            updateAvailableProducts={updateAvailableProducts}
            voucherAmount={voucherAmount}
          />
        </Stack>
      </PageBlock>
      {showInfoReviewModal && (
        <InfoReviewModalContainer
          countries={countries}
          form={form}
          handleEditInfo={handleEditInfo}
          isOpen
          setShowInfoReviewModal={setShowInfoReviewModal}
          travelers={travelers}
        />
      )}
      {isDev && (
        <DevBar>
          <Collection>
            <DebugButton data-testid="fill-traveler-details-form" onClick={() => fillForm(change)}>
              Fill form
            </DebugButton>
            <DebugButton onClick={reset}>Reset Form</DebugButton>
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

TravelerDetailsPage.propTypes = {
  addedProductsNumber: PropTypes.number,
  addToCart: PropTypes.func,
  asyncValidate: PropTypes.func,
  availableExtraProducts: PropTypes.arrayOf(ExtraProduct),
  bounds: PropTypes.arrayOf(BoundNew),
  bundles: PropTypes.arrayOf(BundleProduct),
  bundlesTD: PropTypes.arrayOf(BundleProduct),
  cabinBaggageTripSummary: CabinBaggageTripSummary,
  change: PropTypes.func,
  chargingCurrency: ChargingCurrency,
  checkedBaggageSummary: CheckedBaggageSummary,
  climateCompensationPrice: PropTypes.number,
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      phoneCountryId: PropTypes.string,
      phoneCountryPrefix: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  domesticCountryCode: PropTypes.string,
  fillForm: PropTypes.func,
  form: PropTypes.string,
  handleSubmit: PropTypes.func,
  headerAndInfoHeight: PropTypes.number,
  includedCheckedBaggage: IncludedCheckedBaggage,
  includedProducts: PropTypes.arrayOf(PropTypes.any),
  isAncillaryBundlingEnabled: PropTypes.bool,
  isAnyTouched: PropTypes.bool,
  isDomestic: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMultiStop: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isValid: PropTypes.bool,
  notAvailableProducts: PropTypes.arrayOf(PropTypes.any),
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  personalItemTripSummary: PersonalItemTripSummary,
  products: PropTypes.arrayOf(PropTypes.any),
  reset: PropTypes.func,
  selectionKey: PropTypes.string,
  shareableUrl: PropTypes.string,
  shouldShowEmergencyContactInformation: PropTypes.bool,
  travelerNames: PropTypes.arrayOf(
    PropTypes.shape({
      fullName: PropTypes.string,
      initials: PropTypes.string,
      travelerId: PropTypes.string,
    }),
  ),
  travelerPricesWithoutPaymentDiscounts: PropTypes.arrayOf(TravelerPrice),
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      age: PropTypes.number,
      ageType: PropTypes.string,
      boundIndex: PropTypes.number,
      frequentFlyerCardTypes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.string,
          value: PropTypes.string,
        }),
      ),
      marketingMajorityCarrier: PropTypes.string,
      segments: PropTypes.arrayOf(PropTypes.object),
      shouldShowFrequentFlyerCard: PropTypes.bool,
      travelDocument: PropTypes.shape({
        requireTravelDocumentExpiryDate: PropTypes.oneOf(['OPTIONAL', 'REQUIRED', 'NOT_REQUIRED']),
        shouldShowTravelDocumentCpfNumber: PropTypes.bool,
        shouldShowTravelDocumentDateOfBirth: PropTypes.bool,
        shouldShowTravelDocumentIssueCity: PropTypes.bool,
        shouldShowTravelDocumentIssueDate: PropTypes.bool,
        shouldShowTravelDocumentNationality: PropTypes.bool,
        shouldShowTravelDocumentPassportNumber: PropTypes.bool,
      }),
      travelTime: PropTypes.string,
    }),
  ),
  tripChange: TripChange,
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripId: PropTypes.string,
  tripOperatingCarriers: PropTypes.arrayOf(PropTypes.string),
  tripProviderSystems: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.oneOf([MULTI_STOP, ONE_WAY, RETURN]),
  voucherAmount: PropTypes.shape({
    value: PropTypes.number,
  }),
};

export default TravelerDetailsPage;
